import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, errorText } from '../api';
import { grenzeAnbieten } from '../billtoro/upgrade';
import { ConfirmDialog } from '../components/Dialog';
import { fmtDate } from '../lib/format';
import { eurC } from '../lib/money';
import { useToast } from '../lib/toast';
import { angeboteUrl, angebotUrl, type AngebotVoll } from './AngebotDaten';
import {
  customerBody,
  drittlandArt,
  emptyRow,
  free,
  hinweisAus,
  KUNDEN_ARTEN,
  kundeWerteOf,
  LEERER_KUNDE,
  plusTage,
  positionBody,
  rateAus,
  ratesFor,
  rowOf,
  summen,
  type KundeWerte,
  type PosRow,
} from './formular';
import { KundeWahl } from './KundeWahl';
import { useLeistungen } from './LeistungDaten';
import { PdfDialog } from './PdfDialog';
import { Positionen, SteuerHinweisFeld, Summen, useSteuerHinweis } from './Positionen';
import { darf, fehlerText } from './tarifHinweis';
import type { Kunde, Overview, Rate } from './types';
import { urlId } from './urlId';
import { useWaehrung, WaehrungFeld } from './WaehrungFeld';
import { waehrungAus, type Waehrung } from './waehrung';
import './formular.css';
import './angebote.css';

// wie auf dem Server (angebote.ts): Standard 30 Tage gültig, höchstens ein Jahr
const GUELTIG_TAGE = 30;
const GUELTIG_MAX = 365;

// Formularstand eines Entwurfs (rt_entwuerfe.daten mit art 'angebot'), v: Fassung
interface EntwurfDaten {
  v: 1;
  kunde: KundeWerte;
  rows: Omit<PosRow, 'key' | 'ki' | 'tip' | 'touched'>[];
  site: string;
  intro: string;
  note: string;
  validUntil: string;
  // Hinweis bei 0 % USt (fehlt bei älteren Entwürfen)
  steuerHinweis?: string;
  // Währung und Kurs (Euro je Einheit); fehlt bei älteren Entwürfen: Euro
  waehrung?: Waehrung;
  kurs?: string | null;
}

// Neues Angebot: Kunde (aus der Kundenliste oder neu), Positionen, gültig bis, Einleitung und Hinweis. Vorschau als
// PDF, dann schreiben (eigene Angebotsnummer, danach fest), oder als Entwurf speichern (ohne Prüfung, ohne Nummer).
// Aufruf mit ?vorlage=<id> („Kopieren“ eines Angebots) oder ?entwurf=<id> (Entwurf weiterschreiben). Währung und Kurs
// wie bei der Rechnung (der Kurs steht nicht auf dem Angebot, er dient den Summen in Euro).
export function AngebotForm({ data, reload }: { data: Overview; reload(): Promise<void> }) {
  const navigate = useNavigate();
  const toast = useToast();
  const ku = data.absender.kleinunternehmer;
  const leistungen = useLeistungen(data.firm.id);
  const [params] = useSearchParams();
  // IDs aus der Adresse nur als UUID (urlId.ts)
  const vorlageId = urlId(params.get('vorlage'));
  const entwurfParam = urlId(params.get('entwurf'));
  const [entwurfId, setEntwurfId] = useState<string | null>(entwurfParam);
  const [kunde, setKundeState] = useState<KundeWerte>(LEERER_KUNDE);
  const [rows, setRows] = useState<PosRow[]>(() => [emptyRow()]);
  const drittland = drittlandArt(kunde);
  const steuerHinweis = useSteuerHinweis(rows, ku, data.hinweis0, drittland);
  const wae = useWaehrung({ firmId: data.firm.id, tarif: data.tarif, datum: data.today, preiseDa: rows.some((r) => r.price.trim() !== '') });
  const [site, setSite] = useState('');
  const [intro, setIntro] = useState('');
  const [note, setNote] = useState('');
  const [validUntil, setValidUntil] = useState(() => plusTage(data.today, GUELTIG_TAGE));
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [laden, setLaden] = useState(!!(vorlageId || entwurfId));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  // Übersicht zum Zeitpunkt des Ladens (die Effekte unten laufen nur einmal je Adresse)
  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => () => void (preview && URL.revokeObjectURL(preview)), [preview]);

  const rates: Rate[] = ratesFor(ku);
  const total = summen(rows, ku).gross;
  const satz: Rate = drittland === 'firma' && !ku ? 0 : 19;
  // Kunde außerhalb der EU (Firma) gewählt oder nicht mehr: leere Positionen mit dem passenden Satz
  useEffect(() => {
    setRows((cur) => (cur.some((r) => free(r) && r.rate !== satz) ? cur.map((r) => (free(r) ? { ...r, rate: satz } : r)) : cur));
  }, [satz]);
  // Kunde aus der Liste gewählt: seine Währung (anderer Kunde: Euro)
  const onPick = (k: Kunde | null) => wae.setW(waehrungAus(k?.waehrung));

  // „Kopieren“ aus der Liste: Kunde, Objekt, Positionen und Texte des alten Angebots; Datum und „gültig bis“ neu
  useEffect(() => {
    if (!vorlageId) return;
    let live = true;
    api
      .get<AngebotVoll>(angebotUrl(vorlageId))
      .then((g) => {
        if (!live) return;
        const kundeId = g.kundeId && dataRef.current.kunden.some((k) => k.id === g.kundeId) ? g.kundeId : null;
        setKundeState(kundeWerteOf(g.customer, kundeId));
        // Währung des alten Angebots, der Kurs von heute
        wae.setW(waehrungAus(g.waehrung), false);
        setRows(g.items.length ? g.items.map((i) => rowOf(i)) : [emptyRow()]);
        steuerHinweis.laden(g.items, g.steuerHinweis);
        setSite(g.site ?? '');
        setIntro(g.intro ?? '');
        setNote(g.note ?? '');
        setHinweis(`Kopie von Angebot ${g.number}. Bitte Leistungen und Preise prüfen.`);
      })
      .catch((err) => live && setError(errorText(err)))
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
  }, [vorlageId]);

  // Entwurf weiterschreiben
  useEffect(() => {
    const id = entwurfParam;
    if (!id) return;
    let live = true;
    api
      .get<{ daten: unknown; geaendert: string }>(`/api/rechnungstool/entwurf/${id}`)
      .then((e) => {
        if (!live) return;
        const d = ausEntwurf(e.daten, dataRef.current);
        setKundeState(d.kunde);
        setRows(d.rows.length ? d.rows.map((r) => ({ ...emptyRow(), ...r, touched: true })) : [emptyRow()]);
        steuerHinweis.laden([], d.steuerHinweis);
        setSite(d.site);
        setIntro(d.intro);
        setNote(d.note);
        setValidUntil(d.validUntil);
        wae.laden(d.waehrung ?? 'EUR', d.kurs);
        setHinweis(`Entwurf vom ${fmtDate(e.geaendert)}`);
      })
      .catch((err) => {
        if (!live) return;
        setEntwurfId(null);
        setError(errorText(err));
      })
      .finally(() => live && setLaden(false));
    return () => {
      live = false;
    };
    // nur beim Öffnen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function body() {
    return {
      customer: customerBody(kunde, { xrechnung: false }),
      kundeId: kunde.kundeId,
      entwurfId,
      items: rows.map((r) => positionBody(r, ku)),
      site,
      intro,
      note,
      steuerHinweis: steuerHinweis.body,
      validUntil: validUntil || null,
      ...wae.body,
    };
  }

  async function showPreview() {
    setError(null);
    setBusy(true);
    try {
      const blob = await api.blob(`${angeboteUrl(data.firm.id)}/vorschau`, body());
      setPreview(URL.createObjectURL(blob));
    } catch (err) {
      if (!steuerHinweis.amFeld(err)) setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function write() {
    setBusy(true);
    try {
      const res = await api.post<{ id: string; number: string }>(angeboteUrl(data.firm.id), body());
      // ein neuer Kunde steht jetzt in der Kundenliste
      await reload();
      toast(`Angebot ${res.number} geschrieben`);
      // gleich die PDF, mit „Per E-Mail“ und „Herunterladen“ (Angebote.tsx)
      navigate('/rechnung/angebote', { state: { show: res.id, number: res.number } });
    } catch (err) {
      setConfirm(false);
      if (!steuerHinweis.amFeld(err)) setError(fehlerText(err));
      setBusy(false);
    }
  }

  // Als Entwurf speichern: ohne Prüfung und ohne Nummer, zurück zur Liste (dort oben unter „Entwürfe“)
  async function saveDraft() {
    setError(null);
    setBusy(true);
    const daten: EntwurfDaten = {
      v: 1,
      kunde,
      rows: rows.map(({ key: _k, ki: _ki, tip: _t, touched: _to, ...r }) => r),
      site,
      intro,
      note,
      validUntil,
      steuerHinweis: steuerHinweis.text,
      waehrung: wae.w,
      kurs: wae.body.kurs,
    };
    // Betrag nur für die Liste (unsinnig große Zahlen: ohne Betrag)
    const betrag = Number.isFinite(total) && Math.abs(total) < 1e11 ? total / 100 : 0;
    const b = { art: 'angebot', titel: kunde.name.trim().slice(0, 200), betrag, daten };
    try {
      let saved = false;
      if (entwurfId) {
        try {
          await api.put(`/api/rechnungstool/entwurf/${entwurfId}`, b);
          saved = true;
        } catch (err) {
          // inzwischen gelöscht: als neuer Entwurf speichern
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
      }
      if (!saved) setEntwurfId((await api.post<{ id: string }>(`/api/rechnungstool/${data.firm.id}/entwuerfe`, b)).id);
      toast('Als Entwurf gespeichert');
      navigate('/rechnung/angebote');
    } catch (err) {
      setError(fehlerText(err));
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    // Billtoro: Angebote im Monat schon alle geschrieben: gleich das Fenster „Mehr mit Billtoro“
    if (grenzeAnbieten(data.tarif, 'angeboteMonat')) return;
    setConfirm(true);
  }

  return (
    <form className="rt-card rf" onSubmit={submit} noValidate>
      <div className="rt-form-head">
        <h1>Neues Angebot</h1>
        <Link to="/rechnung/angebote" className="btn small">
          Abbrechen
        </Link>
      </div>
      {hinweis && <p className="rt-vorlage">{hinweis}</p>}

      <h2>Kunde</h2>
      <KundeWahl kunden={data.kunden} items={data.items} today={data.today} value={kunde} onChange={setKundeState} onPick={onPick} />
      <div className="rt-grid">
        <label className="field rt-span4">
          <span>Einsatzort oder Objekt (optional)</span>
          <input value={site} placeholder="z. B. Bürohaus Kehrwieder 7" onChange={(e) => setSite(e.target.value)} />
        </label>
        <label className="field rt-span4">
          <span>Einleitung (optional)</span>
          <textarea
            value={intro}
            rows={2}
            maxLength={1000}
            placeholder="Gerne bieten wir Ihnen folgende Leistungen an:"
            onChange={(e) => setIntro(e.target.value)}
          />
        </label>
      </div>

      <h2>Leistungen</h2>
      <Positionen
        rows={rows}
        onChange={setRows}
        ku={ku}
        rates={rates}
        leistungen={leistungen}
        leistungSpeichern={darf(data.tarif, 'kunden')}
        w={wae.w}
        kurs={wae.kurs}
        satz={satz}
      />
      <SteuerHinweisFeld h={steuerHinweis} auf="dem Angebot" />
      <Summen rows={rows} ku={ku} titel="Angebotssumme" w={wae.w} kurs={wae.kurs} />
      <WaehrungFeld h={wae} />

      <div className="rt-grid">
        <label className="field">
          <span>Gültig bis</span>
          <input
            type="date"
            value={validUntil}
            min={data.today}
            max={plusTage(data.today, GUELTIG_MAX)}
            onChange={(e) => setValidUntil(e.target.value)}
          />
        </label>
        <label className="field rt-span4">
          <span>Hinweis unter den Leistungen (optional)</span>
          <textarea
            value={note}
            rows={2}
            maxLength={1000}
            placeholder="Wir freuen uns auf Ihren Auftrag."
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
      </div>

      {error && <div className="notice bad">{error}</div>}
      <div className="rt-actions rf-actions">
        <button type="button" className="btn rf-entwurf" disabled={busy || laden} onClick={() => void saveDraft()}>
          Als Entwurf speichern
        </button>
        <button type="button" className="btn" disabled={busy || laden} onClick={() => void showPreview()}>
          Vorschau
        </button>
        <button type="submit" className="btn primary" disabled={busy || laden}>
          Angebot schreiben
        </button>
      </div>

      <PdfDialog url={preview} title="Vorschau" onClose={() => setPreview(null)} />
      <ConfirmDialog
        open={confirm}
        title="Angebot jetzt schreiben?"
        text={`Es bekommt die nächste Angebotsnummer und das Datum von heute, gültig bis ${validUntil ? fmtDate(validUntil) : '–'}. Danach lässt es sich nicht mehr ändern, nur kopieren. Angebotssumme: ${eurC(total / 100, wae.w)}.`}
        confirmLabel="Ja, schreiben"
        busy={busy}
        onConfirm={() => void write()}
        onCancel={() => setConfirm(false)}
      />
    </form>
  );
}

// Formularstand aus einem Entwurf lesen, vorsichtig (gespeichert ist, was der Browser geschickt hat): Unbekanntes oder
// Kaputtes fällt auf die Vorgaben zurück. Ein Kunde, der inzwischen aus der Liste entfernt wurde, gilt als neu.
function ausEntwurf(raw: unknown, data: Overview): Omit<EntwurfDaten, 'v'> {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown, d = '') => (typeof v === 'string' ? v : d);
  const k = (o.kunde && typeof o.kunde === 'object' ? o.kunde : {}) as Record<string, unknown>;
  const kunde: KundeWerte = {
    kundeId: typeof k.kundeId === 'string' && data.kunden.some((x) => x.id === k.kundeId) ? k.kundeId : null,
    art: KUNDEN_ARTEN.find(([a]) => a === k.art)?.[0] ?? 'firma',
    name: str(k.name),
    extra: str(k.extra),
    street: str(k.street),
    zip: str(k.zip),
    city: str(k.city),
    country: str(k.country, 'AT'),
    land: str(k.land, 'CH'),
    weltPrivat: k.weltPrivat === true,
    vatId: str(k.vatId),
    email: str(k.email),
    buyerRef: str(k.buyerRef),
  };
  // ältere Entwürfe: Positionen mit „13b“ oder „eu“ werden 0 % (formular.ts rateAus, Hinweis aus hinweisAus)
  const zeilen = (Array.isArray(o.rows) ? o.rows : []).slice(0, 50).map((x) => (x && typeof x === 'object' ? x : {}) as Record<string, unknown>);
  const rows = zeilen.map((r) => ({
    description: str(r.description),
    quantity: str(r.quantity),
    unit: str(r.unit),
    price: str(r.price),
    rate: rateAus(r.rate) ?? 19,
    discount: str(r.discount),
  }));
  // „gültig bis“ aus dem Entwurf nur, wenn es noch geht (sonst wieder 30 Tage ab heute)
  const bis = str(o.validUntil);
  const validUntil = /^\d{4}-\d{2}-\d{2}$/.test(bis) && bis >= data.today ? bis : plusTage(data.today, GUELTIG_TAGE);
  return {
    kunde,
    rows,
    site: str(o.site),
    intro: str(o.intro),
    note: str(o.note),
    validUntil,
    steuerHinweis: hinweisAus(zeilen, o.steuerHinweis, data.hinweis0, drittlandArt(kunde)),
    waehrung: waehrungAus(o.waehrung),
    kurs: typeof o.kurs === 'string' ? o.kurs : null,
  };
}
